import { EMPTY_INSTRUCTION, ISpecialConvertInstruction } from './models/special-convert-instruction.model';
import { ISpecialInstructionsForm } from './models/special-instructions-form.model';

export interface IParseResult {
  value: ISpecialConvertInstruction | null;
  /** Текст исключения JSON.parse. */
  error: string | null;
  /** Распарсилось, но это не объект инструкции. */
  shapeError: boolean;
}

function isPlainObject(value: unknown): boolean {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function dedupe(values: string[]): string[] {
  return values.filter((value, index) => values.indexOf(value) === index);
}

/**
 * Приводит что угодно (ответ бэка, распарсенный JSON пользователя) к валидной инструкции.
 * Мусорные значения выбрасываются молча: экран должен открываться на любых данных.
 */
export function normalizeInstruction(raw: unknown): ISpecialConvertInstruction {
  const source: { [key: string]: unknown } = isPlainObject(raw) ? raw as { [key: string]: unknown } : {};
  const modifierToSizes: ISpecialConvertInstruction['modifierToSizes'] = {};

  if (isPlainObject(source.modifierToSizes)) {
    for (const [modifierId, value] of Object.entries(source.modifierToSizes as object)) {
      const trimmedModifierId = modifierId.trim();
      if (!trimmedModifierId) {
        continue;
      }

      const entry: { [key: string]: unknown } = isPlainObject(value) ? value as { [key: string]: unknown } : {};
      const equivalentSizes: { [oldSizeId: string]: string } = {};

      if (isPlainObject(entry.equivalentSizes)) {
        for (const [from, to] of Object.entries(entry.equivalentSizes as object)) {
          if (from.trim() && typeof to === 'string' && to.trim()) {
            equivalentSizes[from.trim()] = to.trim();
          }
        }
      }

      modifierToSizes[trimmedModifierId] = { equivalentSizes, isRemove: entry.isRemove === true };
    }
  }

  const ignoreModifiers = Array.isArray(source.ignoreModifiers)
    ? dedupe((source.ignoreModifiers as unknown[])
      .filter(id => typeof id === 'string')
      .map((id: string) => id.trim())
      .filter(Boolean))
    : [];

  return { modifierToSizes, ignoreModifiers };
}

/** Вложенный JSON → плоские строки таблицы. Порядок ключей сохраняется. */
export function toRows(instruction: ISpecialConvertInstruction | null | undefined): ISpecialInstructionsForm {
  const normalized = normalizeInstruction(instruction ?? EMPTY_INSTRUCTION);

  return {
    modifiers: Object.entries(normalized.modifierToSizes).map(([modifierId, entry]) => ({
      modifierId,
      isRemove: entry.isRemove,
      sizePairs: Object.entries(entry.equivalentSizes)
        .map(([sourceSizeId, targetSizeId]) => ({ sourceSizeId, targetSizeId })),
    })),
    ignoreModifiers: [...normalized.ignoreModifiers],
  };
}

/**
 * Плоские строки → вложенный JSON для бэка.
 *
 * Пустые id отбрасываются. Дубль модификатора и дубль исходного размера схлопываются
 * «побеждает последний»: объект JSON не умеет хранить два одинаковых ключа. На экране такие
 * строки заранее подсвечены предупреждением.
 */
export function toInstruction(form: ISpecialInstructionsForm): ISpecialConvertInstruction {
  const modifierToSizes: ISpecialConvertInstruction['modifierToSizes'] = {};

  for (const row of form?.modifiers ?? []) {
    const modifierId = (row.modifierId ?? '').trim();
    if (!modifierId) {
      continue;
    }

    const equivalentSizes = { ...(modifierToSizes[modifierId]?.equivalentSizes ?? {}) };

    for (const pair of row.sizePairs ?? []) {
      const from = (pair.sourceSizeId ?? '').trim();
      const to = (pair.targetSizeId ?? '').trim();
      if (!from || !to) {
        continue;
      }
      equivalentSizes[from] = to;
    }

    modifierToSizes[modifierId] = { equivalentSizes, isRemove: row.isRemove === true };
  }

  return {
    modifierToSizes,
    ignoreModifiers: dedupe((form?.ignoreModifiers ?? []).map(id => (id ?? '').trim()).filter(Boolean)),
  };
}

/** Текст для textarea. Отступ 2 — как в json-diff-modal. */
export function stringifyInstruction(instruction: ISpecialConvertInstruction): string {
  return JSON.stringify(instruction, null, 2);
}

export function parseInstructionJson(text: string): IParseResult {
  let parsed: unknown;

  try {
    parsed = JSON.parse(text);
  } catch (exception) {
    return { value: null, error: (exception as Error).message, shapeError: false };
  }

  if (!isPlainObject(parsed)) {
    return { value: null, error: null, shapeError: true };
  }

  return { value: normalizeInstruction(parsed), error: null, shapeError: false };
}

/** Канонический вид для сравнения «изменилось ли»: порядок ключей не должен влиять. */
export function canonicalStringify(instruction: ISpecialConvertInstruction): string {
  return JSON.stringify(sortKeys(instruction));
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortKeys);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const source = value as { [key: string]: unknown };

  return Object.keys(source).sort().reduce((result: { [key: string]: unknown }, key) => {
    result[key] = sortKeys(source[key]);
    return result;
  }, {});
}
